import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";
import type { ApiResponse } from "./api-client.service";

import type {
  Country,
  CountryFilter,
  CountryResponse,
  CountryPayload,
} from "../types/countries.types";

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS.countries;

const failure = (error: unknown, fallback: string) => ({
  success: false,
  statusCode: 500,
  message: error instanceof Error ? error.message : fallback,
  resData: null,
});

export const countriesService = {
  async list(filter: CountryFilter): Promise<CountryResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<CountryResponse>(endpoints.list(params.toString()), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(error, "Failed to load country list.") as unknown as CountryResponse;
    }
  },

  async details(id: number | string): Promise<ApiResponse<Country>> {
    try {
      return await apiClient<ApiResponse<Country>>(endpoints.details(id), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(error, "Failed to load country details.") as unknown as ApiResponse<Country>;
    }
  },

  async create(payload: CountryPayload): Promise<ApiResponse<Country>> {
    try {
      return await apiClient<ApiResponse<Country>>(endpoints.create, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to create country.") as unknown as ApiResponse<Country>;
    }
  },

  async update(id: number | string, payload: Partial<CountryPayload>): Promise<ApiResponse<Country>> {
    try {
      return await apiClient<ApiResponse<Country>>(endpoints.update(id), {
        method: "PATCH",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to update country.") as unknown as ApiResponse<Country>;
    }
  },

  async delete(id: number | string): Promise<ApiResponse<unknown>> {
    try {
      return await apiClient<ApiResponse<unknown>>(endpoints.delete(id), {
        method: "DELETE",
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to delete country.") as unknown as ApiResponse<unknown>;
    }
  },
};
